import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { MapPin, Sparkles, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SupportedLanguage } from "@pulse/i18n";
import {
  CircleTourPreview,
  CIRCLE_PREVIEW_STEPS,
} from "@/components/onboarding/CircleTourPreview";
import { ConciergeTourPreview } from "@/components/onboarding/ConciergeTourPreview";
import { useAuth } from "@/lib/auth";
import {
  ensureConsumerPlatformTrial,
  useConsumerEntitlements,
} from "@/lib/consumerEntitlements";
import { useSaveConsumerOnboarding } from "@/lib/consumerOnboarding";
import { CONSUMER_LANGUAGE_STORAGE_KEY, persistAndApplyLanguage } from "@/lib/languageStorage";
import { useLocation } from "@/lib/location";
import { MUSIC_GENRES } from "@/lib/musicGenres";
import { useSaveProfilePersonal } from "@/lib/profilePersonal";
import {
  NIGHT_STYLE_TAGS,
  toggleTag,
  useSaveUserPreferences,
  useUserPreferences,
} from "@/lib/userPreferences";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/welcome")({
  component: WelcomePage,
});

const WELCOME_CATEGORY_CHIPS = ["Bar", "Club", "Rooftop", "Concert"] as const;
const WELCOME_MUSIC_IDS = [
  "Electronic / Techno",
  "Hip-Hop / R&B",
  "Pop",
  "Indie / Alternative",
  "Latin / Salsa",
  "Jazz / Blues",
] as const;

const LANGS: SupportedLanguage[] = ["en", "de", "fr", "it"];
/** Shared gate steps with mobile: welcome → discovery → circle → concierge → profile+prefs. */
const TOTAL_STEPS = 5;

function firstNameFromUser(name: string | undefined | null, email: string | undefined | null): string {
  const fromName = name?.trim().split(/\s+/)[0];
  if (fromName) return fromName;
  const local = email?.split("@")[0]?.trim();
  if (local) return local.charAt(0).toUpperCase() + local.slice(1);
  return "there";
}

function WelcomePage() {
  const { t, i18n } = useTranslation("consumer");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accessKind, subscriptionSource, refetch: refetchEntitlements } = useConsumerEntitlements();
  const { detectAndSetLocation } = useLocation();
  const { save: saveOnboarding, isSaving: savingOnboarding } = useSaveConsumerOnboarding();
  const { save: savePersonal } = useSaveProfilePersonal();
  const { data: existingPrefs } = useUserPreferences();
  const { save: savePrefs } = useSaveUserPreferences();

  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [circlePreviewStep, setCirclePreviewStep] = useState(0);
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [locationStatus, setLocationStatus] = useState<"idle" | "loading" | "done" | "denied">("idle");
  const [categories, setCategories] = useState<string[]>([]);
  const [musicStyles, setMusicStyles] = useState<string[]>([]);
  const [nightStyles, setNightStyles] = useState<string[]>([]);
  const [dislikeNote, setDislikeNote] = useState("");
  const [finishing, setFinishing] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [skippedFinish, setSkippedFinish] = useState(false);

  const firstName = useMemo(
    () => firstNameFromUser(user?.name, user?.email),
    [user?.name, user?.email],
  );

  useEffect(() => {
    if (!window.localStorage.getItem(CONSUMER_LANGUAGE_STORAGE_KEY)) {
      persistAndApplyLanguage(CONSUMER_LANGUAGE_STORAGE_KEY, "en");
    }
  }, []);

  useEffect(() => {
    if (!existingPrefs) return;
    setCategories(existingPrefs.categories);
    setMusicStyles(existingPrefs.musicStyles);
    setNightStyles(existingPrefs.nightStyles);
    setDislikeNote(existingPrefs.dislikeNote ?? "");
  }, [existingPrefs]);

  const goToStep = (next: number) => {
    if (next < 0 || next >= TOTAL_STEPS) return;
    setVisible(false);
    window.setTimeout(() => {
      setStep(next);
      if (next === 2) setCirclePreviewStep(0);
      setVisible(true);
    }, 160);
  };

  const eliteNote = useMemo(() => {
    const source = subscriptionSource ?? "platform";
    if (accessKind === "expired") return null;
    if (source === "founder" || source === "internal") {
      return t("onboarding.elite.founder");
    }
    if (source === "insider") return t("onboarding.elite.insider");
    if (source === "earlybird") return t("onboarding.elite.earlybird");
    if (accessKind === "trialing") return t("onboarding.elite.trial");
    return t("onboarding.elite.elite");
  }, [accessKind, subscriptionSource, t]);

  const requestLocation = async () => {
    setLocationStatus("loading");
    try {
      await detectAndSetLocation();
      const coords = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
        if (!navigator.geolocation) {
          resolve(null);
          return;
        }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 },
        );
      });
      setLocationStatus(coords ? "done" : "denied");
    } catch {
      setLocationStatus("denied");
    }
  };

  const buildBirthDate = (): string | null => {
    if (!birthDay || !birthMonth || !birthYear) return null;
    const d = Number(birthDay);
    const m = Number(birthMonth);
    const y = Number(birthYear);
    if (!d || !m || !y) return null;
    return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  };

  const prepareThanks = async (skipped: boolean) => {
    setFinishing(true);
    setSkippedFinish(skipped);
    try {
      const birthDate = buildBirthDate();
      if (birthDate) {
        await savePersonal({ birthDate, gender: null });
      }
      await savePrefs({
        musicStyles,
        categories,
        maxBudget: existingPrefs?.maxBudget ?? 50,
        nightStyles,
        dislikeNote: dislikeNote.trim() || null,
        preferredLanguage: (() => {
          const code = i18n.language?.slice(0, 2);
          return code === "en" || code === "de" || code === "fr" || code === "it"
            ? code
            : existingPrefs?.preferredLanguage ?? "en";
        })(),
      });
      // Defer welcome_completed until after thanks so root gate does not leave /welcome early.
      await saveOnboarding({
        welcomeCompleted: false,
        welcomeSkipped: skipped,
        welcomeStep: TOTAL_STEPS,
        tourCompleted: false,
      });
      try {
        await ensureConsumerPlatformTrial();
        let attempts = 0;
        let result = await refetchEntitlements();
        while (
          attempts < 4 &&
          (result as { data?: { accessKind?: string } } | undefined)?.data?.accessKind ===
            "expired"
        ) {
          await new Promise((r) => setTimeout(r, 350));
          result = await refetchEntitlements();
          attempts += 1;
        }
      } catch {
        /* one-shot already consumed */
      }
      setShowThanks(true);
    } catch {
      /* keep prefs step usable */
    } finally {
      setFinishing(false);
    }
  };

  const enterApp = async () => {
    setFinishing(true);
    try {
      await saveOnboarding({
        welcomeCompleted: true,
        welcomeSkipped: skippedFinish,
        welcomeStep: TOTAL_STEPS,
        tourCompleted: true,
      });
      await refetchEntitlements();
      void navigate({ to: "/", replace: true });
    } catch {
      setFinishing(false);
    }
  };

  const advance = () => {
    if (step === 2 && circlePreviewStep < CIRCLE_PREVIEW_STEPS - 1) {
      setCirclePreviewStep((s) => s + 1);
      return;
    }
    if (step < TOTAL_STEPS - 1) goToStep(step + 1);
    else void prepareThanks(false);
  };

  const days = Array.from({ length: 31 }, (_, i) => String(i + 1));
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 80 }, (_, i) => String(currentYear - 16 - i));

  const busy = finishing || savingOnboarding;

  const continueLabel =
    showThanks
      ? t("onboarding.enterPulse")
      : step === 2 && circlePreviewStep < CIRCLE_PREVIEW_STEPS - 1
        ? t("onboarding.tour.circlePreview.continue")
        : step < TOTAL_STEPS - 1
          ? t("onboarding.continue")
          : t("onboarding.letsGo");

  if (showThanks) {
    return (
      <div
        className="relative flex min-h-dvh flex-col bg-[#0B0B0C] text-[#F4F1EA]"
        data-font-group="premium"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,168,75,0.12),_transparent_55%)]" />
        <main className="relative z-10 flex flex-1 flex-col justify-center px-6 pb-8 pt-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
            {t("onboarding.eyebrow.welcome")}
          </p>
          <h1
            className="mt-4 text-4xl leading-tight text-[#F4F1EA]"
            style={{ fontFamily: "var(--font-caslon)" }}
          >
            {t("onboarding.thanksTitle")}
          </h1>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">
            {t("onboarding.thanksBody")}
          </p>
          <p className="mt-6 max-w-sm rounded-xl border border-[#D4A84B]/25 bg-[#D4A84B]/10 px-4 py-3 text-sm text-[#D4A84B]">
            {t("onboarding.thanksTrial")}
          </p>
        </main>
        <footer className="relative z-10 px-6 pb-10">
          <button
            type="button"
            disabled={busy}
            onClick={() => void enterApp()}
            className="w-full rounded-full bg-[#D4A84B] px-6 py-3.5 text-sm font-semibold text-[#0B0B0C] shadow-[0_8px_30px_rgba(212,168,75,0.25)] disabled:opacity-60"
          >
            {continueLabel}
          </button>
        </footer>
      </div>
    );
  }

  return (
    <div
      className="relative flex min-h-dvh flex-col bg-[#0B0B0C] text-[#F4F1EA]"
      data-font-group="premium"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,168,75,0.12),_transparent_55%)]" />

      <header className="relative z-10 flex items-center justify-between px-5 pb-2 pt-12">
        <div className="flex items-center gap-2">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Step ${i + 1}`}
              onClick={() => goToStep(i)}
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                i === step ? "bg-[#D4A84B]" : i < step ? "bg-[#D4A84B]/60" : "bg-white/20",
              )}
            />
          ))}
        </div>
        {step < TOTAL_STEPS - 1 ? (
          <button
            type="button"
            onClick={() => void prepareThanks(true)}
            disabled={busy}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45"
          >
            {t("onboarding.skip")}
          </button>
        ) : (
          <span className="w-10" />
        )}
      </header>

      <main
        className={cn(
          "relative z-10 flex flex-1 flex-col px-6 pb-8 pt-4 transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
      >
        {step === 0 && (
          <section className="flex flex-1 flex-col">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
              {t("onboarding.eyebrow.welcome")}
            </p>
            <div className="mt-6 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A84B]/35 bg-[#D4A84B]/10">
              <Sparkles className="h-6 w-6 text-[#D4A84B]" />
            </div>
            <h1
              className="mt-6 text-4xl leading-tight text-[#F4F1EA]"
              style={{ fontFamily: "var(--font-caslon)" }}
            >
              {t("onboarding.hello", { name: firstName })}
            </h1>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">
              {t("onboarding.helloBody")}
            </p>
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                {t("onboarding.language")}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {LANGS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => persistAndApplyLanguage(CONSUMER_LANGUAGE_STORAGE_KEY, code)}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs font-medium uppercase tracking-wide",
                      i18n.language?.slice(0, 2) === code
                        ? "border-[#D4A84B]/50 bg-[#D4A84B]/15 text-[#D4A84B]"
                        : "border-white/15 text-white/55",
                    )}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {step === 1 && (
          <section className="flex flex-1 flex-col">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
              {t("onboarding.eyebrow.discover")}
            </p>
            <h1
              className="mt-4 text-3xl leading-tight"
              style={{ fontFamily: "var(--font-caslon)" }}
            >
              {t("onboarding.discoverTitle")}
            </h1>
            <p className="mt-2 text-sm text-white/55">{t("onboarding.discoverBody")}</p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#1a1a1c] to-[#0f0f10]">
              <div className="h-28 bg-[linear-gradient(135deg,#2a2418,#121214)]" />
              <div className="px-4 py-3">
                <p className="text-sm font-semibold">{t("onboarding.discoverCardTitle")}</p>
                <p className="mt-1 text-xs text-white/45">{t("onboarding.discoverCardMeta")}</p>
              </div>
            </div>
            <p className="mt-5 text-sm text-white/50">{t("onboarding.tour.discover")}</p>
          </section>
        )}

        {step === 2 && (
          <section className="flex flex-1 flex-col">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
              {t("onboarding.eyebrow.circle")}
            </p>
            <h1
              className="mt-4 text-3xl leading-tight"
              style={{ fontFamily: "var(--font-caslon)" }}
            >
              {t("onboarding.circleTitle")}
            </h1>
            <p className="mt-2 text-sm text-white/55">{t("onboarding.circleBody")}</p>
            <div className="mt-5">
              <CircleTourPreview step={circlePreviewStep} />
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="flex flex-1 flex-col">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
              {t("onboarding.eyebrow.concierge")}
            </p>
            <h1
              className="mt-4 text-3xl leading-tight"
              style={{ fontFamily: "var(--font-caslon)" }}
            >
              {t("onboarding.conciergeTitle")}
            </h1>
            <p className="mt-2 text-sm text-white/55">{t("onboarding.conciergeBody")}</p>
            <div className="mt-5">
              <ConciergeTourPreview />
            </div>
            <p className="mt-5 text-sm text-white/50">{t("onboarding.tour.aiConcierge")}</p>
          </section>
        )}

        {step === 4 && (
          <section className="flex flex-1 flex-col">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4A84B]/80">
              {t("onboarding.eyebrow.profile")}
            </p>
            <div className="mt-6 flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A84B]/35 bg-[#D4A84B]/10">
              <UserRound className="h-6 w-6 text-[#D4A84B]" />
            </div>
            <h1
              className="mt-6 text-3xl leading-tight"
              style={{ fontFamily: "var(--font-caslon)" }}
            >
              {t("onboarding.profileTitle")}
            </h1>
            <p className="mt-2 text-sm text-white/55">{t("onboarding.profileBody")}</p>
            <p className="mt-6 text-sm text-white/55">{t("onboarding.dobOptional")}</p>

            <div className="mt-4 flex gap-2">
              <select
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm"
              >
                <option value="">{t("onboarding.day")}</option>
                {days.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <select
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
                className="flex-1 rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm"
              >
                <option value="">{t("onboarding.month")}</option>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
                className="flex-[1.2] rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm"
              >
                <option value="">{t("onboarding.year")}</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#D4A84B]" />
                <div>
                  <h2 className="text-base font-medium">{t("onboarding.locationTitle")}</h2>
                  <p className="mt-1 text-sm text-white/55">{t("onboarding.locationBody")}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => void requestLocation()}
                disabled={locationStatus === "loading" || locationStatus === "done"}
                className="mt-4 w-full rounded-full border border-[#D4A84B]/40 bg-[#D4A84B]/15 px-4 py-3 text-sm font-semibold text-[#D4A84B]"
              >
                {locationStatus === "done"
                  ? t("onboarding.locationGranted")
                  : locationStatus === "loading"
                    ? t("onboarding.locationLoading")
                    : t("onboarding.locationCta")}
              </button>
              {locationStatus === "denied" ? (
                <p className="mt-2 text-xs text-white/45">{t("onboarding.locationDenied")}</p>
              ) : null}
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
              {t("onboarding.vibeQuestion")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {WELCOME_CATEGORY_CHIPS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setCategories((prev) => toggleTag(prev, tag))}
                  className={cn(
                    "rounded-full border px-3.5 py-2 text-xs font-medium",
                    categories.includes(tag)
                      ? "border-[#D4A84B]/50 bg-[#D4A84B]/15 text-[#D4A84B]"
                      : "border-white/15 text-white/60",
                  )}
                >
                  {t(`onboarding.categories.${tag}`)}
                </button>
              ))}
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
              {t("onboarding.musicQuestion")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {WELCOME_MUSIC_IDS.map((id) => {
                const label = MUSIC_GENRES.find((g) => g.id === id)?.label ?? id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setMusicStyles((prev) => toggleTag(prev, id))}
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-xs",
                      musicStyles.includes(id)
                        ? "border-[#D4A84B]/50 bg-[#D4A84B]/15 text-[#D4A84B]"
                        : "border-white/15 text-white/60",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
              {t("onboarding.nightQuestion")}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {NIGHT_STYLE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setNightStyles((prev) => toggleTag(prev, tag))}
                  className={cn(
                    "rounded-full border px-3.5 py-2 text-xs font-medium",
                    nightStyles.includes(tag)
                      ? "border-[#D4A84B]/50 bg-[#D4A84B]/15 text-[#D4A84B]"
                      : "border-white/15 text-white/60",
                  )}
                >
                  {t(`onboarding.nightStyles.${tag}`)}
                </button>
              ))}
            </div>

            <label className="mt-6 block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
                {t("onboarding.dislikeLabel")}
              </span>
              <input
                value={dislikeNote}
                onChange={(e) => setDislikeNote(e.target.value)}
                placeholder={t("onboarding.dislikePlaceholder")}
                className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm placeholder:text-white/30"
              />
            </label>
            {eliteNote ? (
              <p className="mt-5 rounded-xl border border-[#D4A84B]/25 bg-[#D4A84B]/10 px-3 py-2.5 text-sm text-[#D4A84B]">
                {eliteNote}
              </p>
            ) : null}
          </section>
        )}
      </main>

      <footer className="relative z-10 px-6 pb-10">
        <button
          type="button"
          disabled={busy}
          onClick={advance}
          className="w-full rounded-full bg-[#D4A84B] px-6 py-3.5 text-sm font-semibold text-[#0B0B0C] shadow-[0_8px_30px_rgba(212,168,75,0.25)] disabled:opacity-60"
        >
          {continueLabel}
        </button>
      </footer>
    </div>
  );
}
